import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  Building2,
  Star,
  TrendingUp,
  Eye,
  MapPin,
  Phone,
  Mail,
  Globe,
  Camera,
  Edit3,
  CheckCircle2,
  Crown,
  BarChart3,
  MessageSquare,
  Clock,
  Loader2,
  ExternalLink,
  Plus,
  X,
  Trash2,
  Reply,
  Upload
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContractorTab({ client, hasFeatureAccess, setShowUpgradeModal, stats }) {
  const [contractor, setContractor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Photo upload state
  const fileInputRef = useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  
  // Reviews state
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({ total: 0, average: 0, responded: 0 });
  const [showAddReview, setShowAddReview] = useState(false);
  const [newReview, setNewReview] = useState({ customer_name: '', rating: 5, text: '', service_date: '' });
  const [savingReview, setSavingReview] = useState(false);
  const [respondingTo, setRespondingTo] = useState(null);
  const [responseText, setResponseText] = useState('');

  // Analytics data - use real stats from props or defaults
  const analytics = {
    profile_views: contractor?.profile_views || 0,
    profile_views_change: 0,
    leads_generated: stats?.total_leads || 0,
    leads_change: stats?.leads_this_week || 0,
    // Calculate response rate from contacted vs total leads
    response_rate: stats?.total_leads > 0 
      ? Math.round((stats?.contacted_leads || 0) / stats.total_leads * 100) 
      : null,
    // Response time would need actual tracking - show null if no data
    response_time_mins: null
  };

  useEffect(() => {
    fetchContractorProfile();
    fetchReviews();
  }, [client?.customer_id]);

  const fetchContractorProfile = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/client/contractor`, {
        withCredentials: true
      });
      
      if (response.data.has_listing && response.data.contractor) {
        setContractor(response.data.contractor);
        setEditForm({
          business_name: response.data.contractor.business_name || '',
          phone: response.data.contractor.phone || '',
          email: response.data.contractor.email || '',
          website: response.data.contractor.website || '',
          description: response.data.contractor.description || '',
          address: response.data.contractor.address || '',
          city: response.data.contractor.city || '',
          state: response.data.contractor.state || 'AK',
          services: response.data.contractor.services?.join(', ') || ''
        });
      } else {
        // Set default values if no profile exists
        setContractor({
          business_name: client?.business_name || 'Your Business',
          email: client?.email || '',
          is_premium_listed: client?.subscription_tier === 'premium'
        });
        setEditForm({
          business_name: client?.business_name || '',
          email: client?.email || ''
        });
      }
    } catch (error) {
      console.error('Error fetching contractor profile:', error);
      setContractor({
        business_name: client?.business_name || 'Your Business',
        email: client?.email || '',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/client/contractor/reviews/all`, {
        withCredentials: true
      });
      setReviews(response.data.reviews || []);
      setReviewStats(response.data.stats || { total: 0, average: 0, responded: 0 });
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPG, PNG, WebP, or GIF.');
      return;
    }
    
    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 2MB.');
      return;
    }
    
    setUploadingPhoto(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/client/contractor/photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      
      setContractor(prev => ({ ...prev, logo_url: response.data.logo_url }));
      toast.success('Profile photo uploaded!');
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleDeletePhoto = async () => {
    if (!confirm('Remove your profile photo?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/client/contractor/photo`, {
        withCredentials: true
      });
      setContractor(prev => ({ ...prev, logo_url: null }));
      toast.success('Profile photo removed');
    } catch (error) {
      toast.error('Failed to remove photo');
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/client/contractor`, editForm, {
        withCredentials: true
      });
      setContractor(prev => ({ ...prev, ...editForm }));
      setEditing(false);
      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleAddReview = async () => {
    if (!newReview.customer_name || !newReview.text) {
      toast.error('Please fill in customer name and review text');
      return;
    }
    
    setSavingReview(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/client/contractor/reviews`, newReview, {
        withCredentials: true
      });
      
      setReviews(prev => [response.data.review, ...prev]);
      setReviewStats(prev => ({
        ...prev,
        total: prev.total + 1,
        average: ((prev.average * prev.total) + newReview.rating) / (prev.total + 1)
      }));
      setShowAddReview(false);
      setNewReview({ customer_name: '', rating: 5, text: '', service_date: '' });
      toast.success('Review added!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add review');
    } finally {
      setSavingReview(false);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!confirm('Delete this review?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/client/contractor/reviews/${reviewId}`, {
        withCredentials: true
      });
      setReviews(prev => prev.filter(r => r.review_id !== reviewId));
      fetchReviews(); // Refresh stats
      toast.success('Review deleted');
    } catch (error) {
      toast.error('Failed to delete review');
    }
  };

  const handleRespondToReview = async (reviewId) => {
    if (!responseText.trim()) {
      toast.error('Please enter a response');
      return;
    }
    
    try {
      await axios.post(`${BACKEND_URL}/api/client/contractor/reviews/${reviewId}/respond`, {
        review_id: reviewId,
        response: responseText
      }, { withCredentials: true });
      
      setReviews(prev => prev.map(r => 
        r.review_id === reviewId 
          ? { ...r, contractor_response: responseText, responded_at: new Date().toISOString() }
          : r
      ));
      setRespondingTo(null);
      setResponseText('');
      toast.success('Response added!');
    } catch (error) {
      toast.error('Failed to add response');
    }
  };

  const renderStars = (rating, interactive = false, onSelect = null) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i < rating ? 'text-yellow-400 fill-yellow-400' : 'text-slate-600'} ${interactive ? 'cursor-pointer hover:scale-110 transition-transform' : ''}`}
        onClick={interactive ? () => onSelect(i + 1) : undefined}
      />
    ));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-sky-400" />
      </div>
    );
  }

  const isPremium = client?.subscription_tier === 'premium';

  return (
    <div className="space-y-6">
      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handlePhotoUpload}
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
      />

      {/* Premium Badge / Upgrade Prompt */}
      {!isPremium && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-yellow-500/10 to-amber-500/10 border border-yellow-500/30 rounded-xl p-4 flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            <Crown className="w-5 h-5 text-yellow-400" />
            <div>
              <p className="font-medium text-yellow-400">Unlock Full Contractor Features</p>
              <p className="text-sm text-slate-400">Get a premium listing, review management, and advanced analytics</p>
            </div>
          </div>
          <Button
            onClick={() => setShowUpgradeModal(true)}
            className="bg-gradient-to-r from-yellow-500 to-amber-500 text-black hover:from-yellow-400 hover:to-amber-400"
          >
            <Crown className="w-4 h-4 mr-2" /> Upgrade
          </Button>
        </motion.div>
      )}

      {/* Analytics Overview - Premium Only */}
      {isPremium && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-slate-900/50 border border-slate-800 rounded-xl p-5"
          >
            <div className="flex items-center gap-3 mb-2">
              <Eye className="w-5 h-5 text-sky-400" />
              <span className="text-slate-400 text-sm">Profile Views</span>
            </div>
            <div className="text-3xl font-bold">{analytics.profile_views}</div>
            <div className="text-xs text-green-400 mt-1">+{analytics.profile_views_change} this week</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-slate-900/50 border border-slate-800 rounded-xl p-5"
          >
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="w-5 h-5 text-green-400" />
              <span className="text-slate-400 text-sm">Leads Generated</span>
            </div>
            <div className="text-3xl font-bold text-green-400">{analytics.leads_generated}</div>
            <div className="text-xs text-green-400 mt-1">+{analytics.leads_change} this week</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-slate-900/50 border border-slate-800 rounded-xl p-5"
          >
            <div className="flex items-center gap-3 mb-2">
              <Star className="w-5 h-5 text-yellow-400" />
              <span className="text-slate-400 text-sm">Average Rating</span>
            </div>
            <div className="text-3xl font-bold text-yellow-400">{reviewStats.average || 0}</div>
            <div className="text-xs text-slate-400 mt-1">{reviewStats.total} reviews</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-slate-900/50 border border-slate-800 rounded-xl p-5"
          >
            <div className="flex items-center gap-3 mb-2">
              <Clock className="w-5 h-5 text-purple-400" />
              <span className="text-slate-400 text-sm">Response Rate</span>
            </div>
            <div className="text-3xl font-bold text-purple-400">
              {analytics.response_rate !== null ? `${analytics.response_rate}%` : 'N/A'}
            </div>
            <div className="text-xs text-slate-400 mt-1">
              {analytics.response_time_mins !== null 
                ? `Avg ${analytics.response_time_mins} min` 
                : 'No response data yet'}
            </div>
          </motion.div>
        </div>
      )}

      {/* Business Profile Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Building2 className="w-5 h-5 text-sky-400" />
            <h2 className="font-semibold text-lg">Business Profile</h2>
            {isPremium && (
              <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-yellow-500/10 text-yellow-400 border border-yellow-500/30">
                <Crown className="w-3 h-3" /> Premium Listed
              </span>
            )}
          </div>
          {!editing ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditing(true)}
              className="border-slate-700"
            >
              <Edit3 className="w-4 h-4 mr-1" /> Edit Profile
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditing(false)}
                className="border-slate-700"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSaveProfile}
                disabled={saving}
                className="bg-sky-500 hover:bg-sky-600"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
              </Button>
            </div>
          )}
        </div>

        <div className="p-6">
          {editing ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1">Business Name</label>
                <input
                  type="text"
                  value={editForm.business_name}
                  onChange={(e) => setEditForm({...editForm, business_name: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Phone</label>
                <input
                  type="tel"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Email</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Website</label>
                <input
                  type="url"
                  value={editForm.website}
                  onChange={(e) => setEditForm({...editForm, website: e.target.value})}
                  placeholder="https://yourwebsite.com"
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm text-slate-400 mb-1">Business Description</label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                  rows={3}
                  placeholder="Tell customers about your business..."
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white resize-none"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">City</label>
                <input
                  type="text"
                  value={editForm.city}
                  onChange={(e) => setEditForm({...editForm, city: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">State</label>
                <select
                  value={editForm.state}
                  onChange={(e) => setEditForm({...editForm, state: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                >
                  <option value="AK">Alaska</option>
                  <option value="WA">Washington</option>
                  <option value="OR">Oregon</option>
                  <option value="CA">California</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm text-slate-400 mb-1">Services (comma-separated)</label>
                <input
                  type="text"
                  value={editForm.services}
                  onChange={(e) => setEditForm({...editForm, services: e.target.value})}
                  placeholder="HVAC Repair, Furnace Installation, AC Maintenance"
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                />
              </div>
            </div>
          ) : (
            <div className="flex gap-6">
              {/* Profile Photo */}
              <div className="flex-shrink-0">
                <div className="relative group">
                  {contractor?.logo_url ? (
                    <img 
                      src={contractor.logo_url} 
                      alt="Business" 
                      className="w-24 h-24 rounded-xl object-cover"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-xl bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center">
                      <Building2 className="w-10 h-10 text-white" />
                    </div>
                  )}
                  {/* Photo overlay buttons */}
                  <div className="absolute inset-0 bg-black/50 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingPhoto}
                      className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
                      title="Upload photo"
                    >
                      {uploadingPhoto ? (
                        <Loader2 className="w-4 h-4 text-white animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4 text-white" />
                      )}
                    </button>
                    {contractor?.logo_url && (
                      <button
                        onClick={handleDeletePhoto}
                        className="p-2 bg-red-500/50 rounded-full hover:bg-red-500/70 transition-colors"
                        title="Remove photo"
                      >
                        <Trash2 className="w-4 h-4 text-white" />
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-2 text-center">
                  Max 2MB
                </p>
              </div>

              {/* Profile Info */}
              <div className="flex-1 space-y-3">
                <div>
                  <h3 className="text-xl font-bold">{contractor?.business_name || 'Your Business'}</h3>
                  {contractor?.description && (
                    <p className="text-slate-400 text-sm mt-1">{contractor.description}</p>
                  )}
                </div>

                <div className="flex flex-wrap gap-4 text-sm text-slate-400">
                  {contractor?.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-sky-400" />
                      {contractor.phone}
                    </div>
                  )}
                  {contractor?.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-sky-400" />
                      {contractor.email}
                    </div>
                  )}
                  {contractor?.website && (
                    <a href={contractor.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-sky-400">
                      <Globe className="w-4 h-4 text-sky-400" />
                      Website
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                  {(contractor?.city || contractor?.state) && (
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-sky-400" />
                      {[contractor.city, contractor.state].filter(Boolean).join(', ')}
                    </div>
                  )}
                </div>

                {contractor?.services?.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {(Array.isArray(contractor.services) ? contractor.services : contractor.services.split(',')).map((service, idx) => (
                      <span key={idx} className="px-3 py-1 bg-slate-800 rounded-full text-xs text-slate-300">
                        {service.trim()}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Reviews Section - Premium Only */}
      {isPremium && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden"
        >
          <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <MessageSquare className="w-5 h-5 text-yellow-400" />
              <h2 className="font-semibold text-lg">Customer Reviews</h2>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="flex">{renderStars(Math.round(reviewStats.average))}</div>
                <span className="text-yellow-400 font-bold">{reviewStats.average}</span>
                <span className="text-slate-400 text-sm">({reviewStats.total})</span>
              </div>
              <Button
                onClick={() => setShowAddReview(true)}
                size="sm"
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                <Plus className="w-4 h-4 mr-1" /> Add Review
              </Button>
            </div>
          </div>

          {/* Add Review Form */}
          {showAddReview && (
            <div className="p-6 border-b border-slate-800 bg-slate-800/30">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-medium">Add Customer Review</h3>
                <button onClick={() => setShowAddReview(false)} className="text-slate-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Customer Name *</label>
                  <input
                    type="text"
                    value={newReview.customer_name}
                    onChange={(e) => setNewReview({...newReview, customer_name: e.target.value})}
                    placeholder="John D."
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Service Date</label>
                  <input
                    type="date"
                    value={newReview.service_date}
                    onChange={(e) => setNewReview({...newReview, service_date: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm text-slate-400 mb-1">Rating *</label>
                  <div className="flex gap-1">
                    {renderStars(newReview.rating, true, (r) => setNewReview({...newReview, rating: r}))}
                    <span className="ml-2 text-sm text-slate-400">({newReview.rating}/5)</span>
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm text-slate-400 mb-1">Review Text *</label>
                  <textarea
                    value={newReview.text}
                    onChange={(e) => setNewReview({...newReview, text: e.target.value})}
                    rows={3}
                    placeholder="What did the customer say about your service?"
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white resize-none"
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button
                  onClick={handleAddReview}
                  disabled={savingReview}
                  className="bg-emerald-500 hover:bg-emerald-600"
                >
                  {savingReview ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
                  Add Review
                </Button>
                <Button variant="outline" onClick={() => setShowAddReview(false)} className="border-slate-700">
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Reviews List */}
          {reviews.length === 0 ? (
            <div className="text-center py-12">
              <Star className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-400 mb-2">No reviews yet</h3>
              <p className="text-slate-500 text-sm mb-4">Add your first customer review to showcase your work.</p>
              <Button onClick={() => setShowAddReview(true)} className="bg-emerald-500 hover:bg-emerald-600">
                <Plus className="w-4 h-4 mr-1" /> Add Your First Review
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-slate-800">
              {reviews.map((review) => (
                <div key={review.review_id} className="p-6">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="flex items-center gap-3">
                        <span className="font-medium">{review.customer_name}</span>
                        <div className="flex">{renderStars(review.rating)}</div>
                      </div>
                      <span className="text-xs text-slate-500">
                        {review.service_date || new Date(review.created_at).toLocaleDateString()}
                        {review.source === 'manual' && ' • Added manually'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {review.contractor_response ? (
                        <span className="flex items-center gap-1 text-xs text-green-400">
                          <CheckCircle2 className="w-3 h-3" /> Responded
                        </span>
                      ) : (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => { setRespondingTo(review.review_id); setResponseText(''); }}
                          className="text-xs border-slate-700"
                        >
                          <Reply className="w-3 h-3 mr-1" /> Reply
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteReview(review.review_id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-slate-300">{review.text}</p>
                  
                  {/* Contractor Response */}
                  {review.contractor_response && (
                    <div className="mt-3 ml-4 pl-4 border-l-2 border-sky-500/30">
                      <p className="text-sm text-slate-400 mb-1">
                        <span className="text-sky-400 font-medium">Your response:</span>
                      </p>
                      <p className="text-sm text-slate-300">{review.contractor_response}</p>
                    </div>
                  )}
                  
                  {/* Response Form */}
                  {respondingTo === review.review_id && (
                    <div className="mt-3 p-3 bg-slate-800/50 rounded-lg">
                      <textarea
                        value={responseText}
                        onChange={(e) => setResponseText(e.target.value)}
                        rows={2}
                        placeholder="Write your response..."
                        className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm resize-none mb-2"
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => handleRespondToReview(review.review_id)} className="bg-sky-500 hover:bg-sky-600">
                          Post Response
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setRespondingTo(null)} className="border-slate-700">
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </motion.div>
      )}

      {/* Listing Status */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-slate-800/30 border border-slate-700 rounded-xl p-6"
      >
        <div className="flex items-start gap-3">
          <BarChart3 className="w-5 h-5 text-sky-400 mt-0.5" />
          <div>
            <h3 className="font-medium text-sky-400">
              {isPremium ? 'Premium Directory Listing' : 'Standard Directory Listing'}
            </h3>
            <p className="text-sm text-slate-400 mt-1">
              {isPremium 
                ? 'Your business appears at the top of search results with enhanced visibility and a verified badge.'
                : 'Your business is listed in our contractor directory. Upgrade to Premium for priority placement and enhanced features.'}
            </p>
            {!isPremium && (
              <Button
                onClick={() => setShowUpgradeModal(true)}
                variant="outline"
                size="sm"
                className="mt-3 border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10"
              >
                <Crown className="w-4 h-4 mr-2" /> Get Premium Listing
              </Button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
